import { axiosInstance } from "./axios";
import { AxiosResponse } from "axios";
import { IWorkspace, IWorkspaceStructure } from "dto/IWorkspace";
import { IGenericResponse } from "dto/IGenericResponse";
import useCustomMutation from "hooks/useCustomMutation";
import useCustomQuery from "hooks/useCustomQuery";
import { IParcelsSummary } from "dto/IParcelsSummary";
import { IInvoice } from "dto/IInvoice";
import {
  ISubscriptionsView,
  IUpdateSubscriptionRequest,
  IUpdateSubscriptionsBatchRequest,
} from "dto/ISubscriptionsView";
import { IBillingInfo, ISaveBillingInfoRequest } from "dto/IBillingInfo";
import { IPaymentMethodView, ISetupIntentResult } from "dto/IPaymentMethod";
import { IPaymentsListView } from "dto/IPayment";
import {
  ITransferUserWorkItemsRequest,
  ITransferUserWorkItemsResponse,
  IUserWorkItemsReport,
} from "dto/Workspace/IUserWorkItems";
import {
  ISaveWorkspaceJobTitle,
  IWorkspaceJobTitle,
  IWorkspaceJobTitlesResponse,
} from "dto/Workspace/IWorkspaceJobTitle";
import { EBudgetsAccessLevel, EUserRole } from "dto/Access/IMemberAccess";

const queryKeys = {
  getWorkspace: (workspaceId: string) => [
    "workspaceService.getWorkspace",
    workspaceId,
  ],
  getWorkspaces: "workspaceService.getWorkspaces",
  getParcelsSummary: (workspaceId: string) => [
    "workspaceService.getParcelsSummary",
    workspaceId,
  ],
  getPaymentsSummary: (workspaceId: string) => [
    "workspaceService.getPaymentsSummary",
    workspaceId,
  ],
  getInvoices: (workspaceId: string) => [
    "workspaceService.getInvoices",
    workspaceId,
  ],
  getSubscriptions: (workspaceId: string) => [
    "workspaceService.getSubscriptions",
    workspaceId,
  ],
  getBillingInfo: (workspaceId: string) => [
    "workspaceService.getBillingInfo",
    workspaceId,
  ],
  getPaymentMethod: (workspaceId: string) => [
    "workspaceService.getPaymentMethod",
    workspaceId,
  ],
  getPayments: (workspaceId: string) => [
    "workspaceService.getPayments",
    workspaceId,
  ],
  // getCurrentWorkspace usunięty — bieżący zespół to dziś firma zalogowanego
  // (CompanyContext, docs/model-kluczy-firmowych.md), dociągana przez api/company/current
  // + getWorkspace(company.Id) (ten sam wiersz bazy, patrz components/ProtectedRoute).
  getInvitation: (id: string) => ["workspaceService.getInvitation", id],
  getUserWorkItems: (userId: string) => [
    "workspaceService.getUserWorkItems",
    userId,
  ],
  getJobTitles: "workspaceService.getJobTitles",
};

const getWorkspaces = (): Promise<{ data: IGenericResponse<IWorkspace[]> }> => {
  return axiosInstance.get("/workspaces");
};

const useGetWorkspaces = () => {
  return useCustomQuery(queryKeys.getWorkspaces, () => getWorkspaces());
};

const getWorkspace = (workspaceId: string): Promise<{ data: IWorkspace }> => {
  if (!workspaceId.length) {
    //@ts-ignore
    return { data: [] } as AxiosResponse;
  }

  return axiosInstance.get(`/workspace?id=${workspaceId}`);
};

const useGetWorkspace = (workspaceId: string) => {
  return useCustomQuery(queryKeys.getWorkspace(workspaceId), () =>
    getWorkspace(workspaceId),
  );
};

const getParcelsSummary = (
  workspaceId: string,
): Promise<{ data: IParcelsSummary }> => {
  if (!workspaceId.length) {
    //@ts-ignore
    return { data: [] } as AxiosResponse;
  }

  return axiosInstance.get(`/payments/parcels?id=${workspaceId}`);
};

const useGetParcelsSummary = (workspaceId: string) => {
  return useCustomQuery(queryKeys.getParcelsSummary(workspaceId), () =>
    getParcelsSummary(workspaceId),
  );
};

const getPaymentsSummary = (
  workspaceId: string,
): Promise<{ data: IParcelsSummary }> => {
  // Pusty workspaceId → rozliczenia subskrypcji prywatnej (BE rozpozna brak id).
  const query = workspaceId?.length ? `?id=${workspaceId}` : "";
  return axiosInstance.get(`/payments/summary${query}`);
};

const useGetPaymentsSummary = (workspaceId: string) => {
  return useCustomQuery(queryKeys.getPaymentsSummary(workspaceId), () =>
    getPaymentsSummary(workspaceId),
  );
};

const getInvoices = (workspaceId: string): Promise<{ data: IInvoice[] }> => {
  // Pusty workspaceId → faktury subskrypcji prywatnej (BE rozpozna brak id).
  const query = workspaceId?.length ? `?id=${workspaceId}` : "";
  return axiosInstance.get(`/payments/invoices${query}`);
};

const useGetInvoices = (workspaceId: string) => {
  return useCustomQuery(queryKeys.getInvoices(workspaceId), () =>
    getInvoices(workspaceId),
  );
};

/** Lista płatności profilu (status oczekująca/opłacona/wymaga potwierdzenia/nieudana). */
const getPayments = (
  workspaceId: string,
): Promise<{ data: IPaymentsListView }> => {
  const query = workspaceId?.length ? `?id=${workspaceId}` : "";
  return axiosInstance.get(`/payments/payments${query}`);
};

const useGetPayments = (workspaceId: string) => {
  return useCustomQuery(queryKeys.getPayments(workspaceId), () =>
    getPayments(workspaceId),
  );
};

/**
 * Widok subskrypcji dla zakładki Plany. Pusty workspaceId → tryb prywatny
 * (BE zwraca subskrypcję bieżącego użytkownika); ustawiony → tryb zespołowy.
 */
const getSubscriptions = (
  workspaceId: string,
): Promise<{ data: ISubscriptionsView }> => {
  const query = workspaceId?.length ? `?id=${workspaceId}` : "";
  return axiosInstance.get(`/payments/subscriptions${query}`);
};

const useGetSubscriptions = (workspaceId: string) => {
  return useCustomQuery(queryKeys.getSubscriptions(workspaceId), () =>
    getSubscriptions(workspaceId),
  );
};

const updateSubscription = (
  payload: IUpdateSubscriptionRequest,
): Promise<AxiosResponse> => {
  return axiosInstance.post("/payments/subscription", payload);
};

const getBillingInfo = (
  workspaceId: string,
): Promise<{ data: IBillingInfo }> => {
  const query = workspaceId?.length ? `?id=${workspaceId}` : "";
  return axiosInstance.get(`/payments/billing-info${query}`);
};

const useGetBillingInfo = (workspaceId: string) => {
  return useCustomQuery(queryKeys.getBillingInfo(workspaceId), () =>
    getBillingInfo(workspaceId),
  );
};

const saveBillingInfo = (
  payload: ISaveBillingInfoRequest,
): Promise<AxiosResponse> => {
  return axiosInstance.post("/payments/billing-info", payload);
};

const useSaveBillingInfo = () => {
  return useCustomMutation(saveBillingInfo);
};

const getPaymentMethod = (
  workspaceId: string,
): Promise<{ data: IPaymentMethodView | null }> => {
  const query = workspaceId?.length ? `?id=${workspaceId}` : "";
  return axiosInstance.get(`/payments/method${query}`);
};

const useGetPaymentMethod = (workspaceId: string) => {
  return useCustomQuery(queryKeys.getPaymentMethod(workspaceId), () =>
    getPaymentMethod(workspaceId),
  );
};

const createSetupIntent = (
  workspaceId?: string,
): Promise<{ data: ISetupIntentResult }> => {
  const query = workspaceId?.length ? `?id=${workspaceId}` : "";
  return axiosInstance.post(`/payments/method/setup-intent${query}`);
};

const attachPaymentMethod = (payload: {
  WorkspaceId?: string | null;
  PaymentMethodId: string;
}): Promise<{ data: IPaymentMethodView }> => {
  return axiosInstance.post("/payments/method/attach", payload);
};

const removePaymentMethod = (workspaceId?: string): Promise<AxiosResponse> => {
  const query = workspaceId?.length ? `?id=${workspaceId}` : "";
  return axiosInstance.delete(`/payments/method${query}`);
};

/** Pobiera PDF z wyciągiem rozliczeniowym członka za dany okres. */
const downloadBilling = (params: {
  workspaceId?: string;
  memberUserId?: string;
  year: number;
  month: number;
}): Promise<{ data: Blob }> => {
  const query = new URLSearchParams();
  if (params.workspaceId) query.set("id", params.workspaceId);
  if (params.memberUserId) query.set("memberUserId", params.memberUserId);
  query.set("year", String(params.year));
  query.set("month", String(params.month));

  return axiosInstance.get(`/payments/billing?${query.toString()}`, {
    responseType: "blob",
  });
};

/** Pobiera PDF pojedynczej faktury subskrypcyjnej. */
const downloadInvoice = (invoiceId: string): Promise<{ data: Blob }> => {
  return axiosInstance.get(`/payments/invoice?id=${invoiceId}`, {
    responseType: "blob",
  });
};

const useUpdateSubscription = () => {
  return useCustomMutation(updateSubscription);
};

const updateSubscriptionsBatch = (
  payload: IUpdateSubscriptionsBatchRequest,
): Promise<AxiosResponse> => {
  return axiosInstance.post("/payments/subscriptions/batch", payload);
};

const useUpdateSubscriptionsBatch = () => {
  return useCustomMutation(updateSubscriptionsBatch);
};

export interface ICreateWorkspace {
  Name: string;
  /** Para kluczy zespołu (base64). Klucz prywatny zaszyfrowany kluczem intermediate administratora. */
  PublicKey?: string;
  PrivateKey?: string;
  /** Klucze profilu zespołowego administratora (base64), tworzone jak przy dołączaniu z zaproszenia. */
  ProfilePublicKey?: string;
  ProfilePrivateKey?: string;
  /** Klucz prywatny profilu zaszyfrowany kluczem publicznym zespołu (do przejęcia profilu). */
  ProfileSharedPrivateKey?: string;
}

const createWorkspace = (
  payload: ICreateWorkspace,
): Promise<{ data: IWorkspace }> => {
  return axiosInstance.post("/workspace", payload);
};

const useCreateWorkspace = () => {
  return useCustomMutation(createWorkspace);
};

export interface IInviteToWorkspace {
  WorkspaceId: string;
  RoleId: string;
  Emails?: string[];
  Email?: string;
  Name?: string;
  PrivateAccount?: boolean;
  /** Komórka organizacyjna zapraszanego (WorkspaceStructureItems.Id). */
  StructureId?: string | null;
  /** Stanowisko zapraszanego. */
  PositionTitle?: string | null;
}

const inviteToWorkspace = (payload: IInviteToWorkspace) => {
  return axiosInstance.post("workspace/member/invite", payload);
};

const useInviteToWorkspace = () => {
  return useCustomMutation(inviteToWorkspace);
};

export interface IRemoveUser {
  Id: string;
}

const removeUser = (payload: IRemoveUser) => {
  return axiosInstance.delete("/workspace/member", {
    data: payload,
  });
};

const useRemoveUser = () => {
  return useCustomMutation(removeUser);
};

// Model kluczy firmowych, F7 (Q8): getSwitches/switchUser (GET /subaccount/switches,
// POST /subaccount/switch) usunięte razem z resztą subkont — FE nie miało ani jednego
// konsumenta tego przełącznika, a BE oddawał tamtędy cały klucz pośredni właściciela.

export interface ICreateWorkspaceRole {
  WorkspaceId: string;
  Name: string;
  Privileges: string[];
}

const createWorkspaceRole = (payload: ICreateWorkspaceRole) => {
  return axiosInstance.post("/workspace/role", payload);
};

const useCreateWorkspaceRole = () => {
  return useCustomMutation(createWorkspaceRole);
};

export interface IEditWorkspaceRole {
  Id: string;
  Name: string;
  Privileges: string[];
}

const editWorkspaceRole = (payload: IEditWorkspaceRole) => {
  return axiosInstance.put("/workspace/role", payload);
};

const useEditWorkspaceRole = () => {
  return useCustomMutation(editWorkspaceRole);
};

export interface IDeleteRole {
  Id: string;
}

const deleteRole = (payload: IDeleteRole) => {
  return axiosInstance.delete("/workspace/role", {
    data: payload,
  });
};

const useDeleteRole = () => {
  return useCustomMutation(deleteRole);
};

export interface ICreateStructure {
  WorkspaceId: string;
  Name: string;
  ParentId?: string | null;
  Symbol?: string | null;
  ManagerUserId?: string | null;
}

const createStructure = (
  payload: ICreateStructure,
): Promise<{ data: IWorkspaceStructure }> => {
  return axiosInstance.post("/workspace/structure/item", payload);
};

const useCreateStructure = () => {
  return useCustomMutation(createStructure);
};

export interface IUpdateStructure {
  Id: string;
  Name: string;
  Symbol?: string | null;
  ManagerUserId?: string | null;
}

const updateStructure = (
  payload: IUpdateStructure,
): Promise<{ data: IWorkspaceStructure }> => {
  return axiosInstance.put("/workspace/structure/item", payload);
};

const useUpdateStructure = () => {
  return useCustomMutation(updateStructure);
};

const removeStructure = (payload: { Id: string }) => {
  return axiosInstance.delete("/workspace/structure/item", { data: payload });
};

const useRemoveStructure = () => {
  return useCustomMutation(removeStructure);
};

export interface IMoveStructure {
  Id: string;
  ParentId: string | null;
}

const moveStructure = (payload: IMoveStructure) => {
  return axiosInstance.post("/workspace/structure/item/move", payload);
};

const useMoveStructure = () => {
  return useCustomMutation(moveStructure);
};

export interface IMoveMembershipStructure {
  Id: string;
  ParentId: string | null;
}

const moveMembershipStructure = (payload: IMoveMembershipStructure) => {
  return axiosInstance.post("/workspace/member/move", payload);
};

const useMoveMembershipStructure = () => {
  return useCustomMutation(moveMembershipStructure);
};

export interface IApproveInvitation {
  Id?: string;
  Key?: string;
}

const approveInvitation = (payload: IApproveInvitation) => {
  return axiosInstance.post("/workspace/invitation/approve", payload);
};

const useApproveInvitation = () => {
  return useCustomMutation(approveInvitation);
};

export interface IRejectInvitation {
  Id?: string;
  Key?: string;
}

const rejectInvitation = (payload: IRejectInvitation) => {
  return axiosInstance.post("/workspace/invitation/reject", payload);
};

const useRejectInvitation = () => {
  return useCustomMutation(rejectInvitation);
};

export interface IWorkspaceSwitch {
  Id: string;
}

const workspaceSwitch = (
  payload: IWorkspaceSwitch,
): Promise<{ data: { success: boolean } }> => {
  return axiosInstance.post("/workspace/switch", payload);
};

const useWorkspaceSwitch = () => {
  return useCustomMutation(workspaceSwitch);
};

export interface IWorkspaceEditMember {
  Id: string;
  RoleId?: string;
  /** Stanowisko (tekst); pomiń, żeby nie zmieniać. */
  PositionTitle?: string | null;
  /** Komórka — brana pod uwagę tylko przy SetStructure = true (null = bez komórki). */
  StructureId?: string | null;
  SetStructure?: boolean;
  /** Stopień służbowy ze słownika — brany pod uwagę tylko przy SetJobGrade = true. */
  JobGradeId?: string | null;
  SetJobGrade?: boolean;
  // Metryka pracownika z Magistratu — pominięte pole = bez zmian.
  UserRole?: EUserRole;
  LocalLawAccess?: number;
  BudgetsAccessLevel?: EBudgetsAccessLevel;
  SpecialCasePermissions?: number;
  IsKSeFHandler?: boolean;
}

const workspaceEditMember = (payload: IWorkspaceEditMember) => {
  return axiosInstance.put("/workspace/member", payload);
};

const useWorkspaceEditMember = () => {
  return useCustomMutation(workspaceEditMember);
};

// ── Słownik kadrowy firmy: stopnie i stanowiska służbowe ────────────────────

const getJobTitles = (): Promise<{ data: IWorkspaceJobTitlesResponse }> => {
  return axiosInstance.get("/workspace/job-titles");
};

const useGetJobTitles = (enabled = true) => {
  return useCustomQuery(queryKeys.getJobTitles, () => getJobTitles(), {
    enabled,
  });
};

const saveJobTitle = (
  payload: ISaveWorkspaceJobTitle,
): Promise<{ data: IWorkspaceJobTitle }> => {
  return axiosInstance.post("/workspace/job-title", payload);
};

const useSaveJobTitle = () => {
  return useCustomMutation(saveJobTitle);
};

const removeJobTitle = (id: string) => {
  return axiosInstance.delete("/workspace/job-title", { data: { Id: id } });
};

const useRemoveJobTitle = () => {
  return useCustomMutation(removeJobTitle);
};

const getInvitation = (invitationId: string) => {
  return axiosInstance.get(`/invitation?id=${invitationId}`);
};

const useGetInvitation = (invitationId: string) => {
  return useCustomQuery(queryKeys.getInvitation(invitationId), () =>
    getInvitation(invitationId),
  );
};

export interface IRegister {
  InvitationId?: string;
  Email: string;
  FirstName?: string;
  LastName?: string;
  Password: string;
  RepeatPassword: string;
  AcceptNotaRules: boolean;
  AcceptRules: boolean;
  ConnectionId?: string;
  LegalPerson: boolean;
}

const registerInWorkspace = (payload: IRegister) => {
  return axiosInstance.post("/register", payload);
};

const useRegisterInWorkspace = () => {
  return useCustomMutation(registerInWorkspace);
};

export interface ITakeoverProfile {
  WorkspaceId: string;
  ProfileId: string;
  NewUserId: string;
  /** Klucz prywatny profilu przeszyfrowany kluczem publicznym użytkownika docelowego (base64). */
  PrivateKey: string;
}

const takeoverProfile = (payload: ITakeoverProfile) => {
  return axiosInstance.post("/workspace/profile/takeover", payload);
};

const useTakeoverProfile = () => {
  return useCustomMutation(takeoverProfile);
};

export interface IVerify {
  Token: string;
  Code: string;
}

const verifyEmail = (payload: IVerify) => {
  return axiosInstance.post("/register/verifyEmail", payload);
};

const useVerifyEmail = () => {
  return useCustomMutation(verifyEmail);
};

// Synchronizacja osob ze schematu (orgpersons/sync) USUNIETA: jedno zrodlo prawdy o osobach
// to lista czlonkow zespolu (WorkspaceMembers) + ich komorka (StructureId) i stanowisko (PositionTitle).

// Transfer danych (zawartości) członka zespołu — port UserWorkItemsTransferWizardWindow
// z Magistratu (menu „more" na karcie członka). Raport liczy zawartość per grupa (teczki
// rozbite na kody JRWA), transfer przenosi wybrane grupy / kody wskazanym odbiorcom.
const getUserWorkItemsReport = (
  userId: string,
): Promise<AxiosResponse<IUserWorkItemsReport>> => {
  return axiosInstance.get(`/workspace/member/work-items?userId=${userId}`);
};

const useGetUserWorkItemsReport = (userId: string) => {
  return useCustomQuery(
    queryKeys.getUserWorkItems(userId),
    () => getUserWorkItemsReport(userId),
    { enabled: !!userId, cacheTime: 0 },
  );
};

const transferUserWorkItems = (
  payload: ITransferUserWorkItemsRequest,
): Promise<AxiosResponse<ITransferUserWorkItemsResponse>> => {
  return axiosInstance.post("/workspace/member/work-items/transfer", payload);
};

const useTransferUserWorkItems = () => {
  return useCustomMutation(transferUserWorkItems);
};

export default {
  useCreateWorkspace,
  useGetWorkspaces,
  useGetWorkspace,
  useInviteToWorkspace,
  useRemoveUser,
  useCreateWorkspaceRole,
  useEditWorkspaceRole,
  useDeleteRole,
  useCreateStructure,
  useUpdateStructure,
  useRemoveStructure,
  useMoveStructure,
  useMoveMembershipStructure,
  useApproveInvitation,
  useRejectInvitation,
  useWorkspaceSwitch,
  useWorkspaceEditMember,
  useGetInvitation,
  useRegisterInWorkspace,
  useVerifyEmail,
  useGetParcelsSummary,
  useGetPaymentsSummary,
  useGetInvoices,
  useGetPayments,
  useGetSubscriptions,
  useUpdateSubscription,
  useUpdateSubscriptionsBatch,
  useGetBillingInfo,
  useSaveBillingInfo,
  useGetPaymentMethod,
  createSetupIntent,
  attachPaymentMethod,
  removePaymentMethod,
  downloadBilling,
  downloadInvoice,
  useTakeoverProfile,
  useGetUserWorkItemsReport,
  useTransferUserWorkItems,
  useGetJobTitles,
  useSaveJobTitle,
  useRemoveJobTitle,
};
